import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'

import PasswordEyeInput from '@/components/forms/PasswordEyeInput/PasswordEyeInput'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import useAuthStore, { type ChangePasswordRequest } from '@/stores/AuthStore'
import routes from '@/routes/routes'

import './ChangePassword.css'
import '../Auth/Auth.css'
import '@/styles/General.css'

const ChangePassword = () => {
    const navigate = useNavigate()
    const { t } = useTranslation('common')
    const { changePasswordLogged, isLoading, isLogged } = useAuthStore()

    const [formData, setFormData] = useState<ChangePasswordRequest>({
        old_password: '',
        new_password1: '',
        new_password2: '',
    })

    useEffect(() => {
        if (!isLogged) {
            navigate(routes.auth)
        }
    }, [isLogged, navigate])

    const handleFormDataChange = (key: string, value: string) => {
        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const validateForm = (formData: ChangePasswordRequest) => {
        const { old_password, new_password1, new_password2 } = formData
        
        if (!old_password || !new_password1 || !new_password2) {
            return { isValid: false, message: t('missing_fields') }
        }
        
        // Password confirmation validation
        if (new_password1 !== new_password2) {
            return { isValid: false, message: t('passwords_do_not_match') }
        }

        // Check if new password is different from old password
        if (old_password === new_password1) {
            return { isValid: false, message: 'New password must be different from current password' }
        }
        
        // Password strength validation (optional)
        if (new_password1.length < 8) {
            return { isValid: false, message: 'Password must be at least 8 characters long' }
        }
        
        return { isValid: true, message: '' }
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault()

        const validation = validateForm(formData)
        if (!validation.isValid) {
            toast.error(validation.message)
            return
        }

        try {
            const success = await changePasswordLogged(formData)
            if (success) {
                navigate(routes.profile)
                return
            }
        } catch (error) {
            console.error('Change password error:', error)
        }
    }

    return (
        <div className="auth-page">
            <div className="auth-container">
                {/* Welcome Section */}
                <div className="auth-welcome">
                    <img className="auth-logo-persistent" src="/domber.png" alt="Logo Domber" />
                    
                    <div className="auth-welcome-content">
                        <h1 className="auth-welcome-title auth-form-title">{t('change_password')}</h1>
                        <p className="auth-welcome-tagline">Mantén tu cuenta segura</p>
                    </div>
                </div>

                {/* Footer */}
                <div className="auth-footer-global">
                    © 2025 - Domber
                </div>

                {/* Form */}
                <div className="auth-form-container">
                    <div className="auth-card-body">
                        <form className="auth-form" onSubmit={handleSubmit}>
                            <PasswordEyeInput
                                name="old_password"
                                value={formData.old_password}
                                setValue={(value) => handleFormDataChange('old_password', value)}
                                label={t('old_password_label')}
                                placeholder={t('old_password_placeholder')}
                            />
                            
                            <PasswordEyeInput
                                name="new_password1"
                                value={formData.new_password1}
                                setValue={(value) => handleFormDataChange('new_password1', value)}
                                label={t('password1_label')}
                                placeholder={t('password1_placeholder')}
                            />
                            
                            <PasswordEyeInput
                                name="new_password2"
                                value={formData.new_password2}
                                setValue={(value) => handleFormDataChange('new_password2', value)}
                                label={t('password2_label')}
                                placeholder={t('password2_placeholder')}
                            />

                            <Button
                                variant="primary"
                                size="lg"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? <Spinner variant="secondary" size="xs" /> : t('change_password')}
                            </Button>
                        </form>
                        <div className="auth-separator"></div>
                        <div className="auth-links">
                            <Button
                                variant="secondary"
                                size="md"
                                onClick={() => navigate(routes.profile)}
                                className="w-full"
                                disabled={isLoading}
                            >
                                Volver al perfil
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ChangePassword
